import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule, MatLabel } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

import { EntrySkeletonComponent } from '../shared/entry-skeleton/entry-skeleton.component';
import { AuthService, ThemeService } from '../../../core/services';
import * as Paths from '../../../core/constants';
import { FormErrorStateMatcher } from '../../../core/utils/FormErrorStateMatcher';
import { FocusDirective } from '../../main/shared/directives/focus.directive';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'dbx-forget-password',
  imports: [
    CommonModule,
    EntrySkeletonComponent,
    ReactiveFormsModule,
    TranslateModule,
    MatFormFieldModule,
    MatInputModule,
    MatLabel,
    MatProgressSpinner,
    RouterLink,
    FocusDirective,
    MatButton,
  ],
  templateUrl: './forget-password.component.html',
  styleUrl: './forget-password.component.scss',
  host: {
    class: 'entry-page',
  },
  standalone: true,
})
export class ForgetPasswordComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly themeService = inject(ThemeService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastrService = inject(ToastrService);
  private readonly translateService = inject(TranslateService);

  loading = signal<boolean>(false);
  submitted = signal(false);

  errorStateMatcher: FormErrorStateMatcher = new FormErrorStateMatcher(this.submitted);

  form = this.formBuilder.group({
    username: ['', [Validators.required, Validators.email]],
  });

  readonly Paths = Paths;

  get username(): AbstractControl<string | null, string | null> | null {
    return this.form.get('username');
  }

  ngOnInit(): void {
    this.themeService.forceDarkTheme();
    this.route.queryParams.subscribe((params) => {
      const email = params['email'];
      if (email) {
        this.form.patchValue({ username: email });
      }
    });
  }

  ngOnDestroy(): void {
    this.themeService.init();
  }

  submit() {
    this.submitted.set(true);
    if (this.form.invalid) {
      return;
    }

    this.loading.set(true);
    this.authService.forgetPassword(this.username?.value ?? '').subscribe({
      next: () => {
        this.loading.set(false);
        this.toastrService.info(this.translateService.instant('forgetPassword.alerts.forgetPasswordMailSent'));
        this.router.navigate([Paths.LOGIN_PATH]).then();
      },
      error: () => {
        this.loading.set(false);
        this.toastrService.error(this.translateService.instant('common.alerts.genericError'));
      },
    });
  }
}
